import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ear, MessageSquareText, Phone, ShieldAlert, Voicemail, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { formatPhone } from '@/lib/phoneDisplay';
import { screenCallStore, useScreenCall } from '@/lib/screenCallStore';

interface ScreenView {
  active: boolean;
  state: 'screening' | 'waiting' | 'connecting' | 'ended';
  transcript: string;
  final: boolean;
  decision: 'answer' | 'voicemail' | 'reply' | 'spam' | null;
}

const STATUS: Record<ScreenView['state'], string> = {
  screening: 'Assistant is asking who is calling…',
  waiting: 'Caller is on hold, waiting for you',
  connecting: 'Connecting you…',
  ended: 'Screening ended',
};

/**
 * Live screening of an inbound call (web dashboard and /mobile). Mounted once by
 * GlobalVoiceProvider; visible while screenCallStore has an active target.
 */
export default function ScreenCallPanel() {
  const { active } = useScreenCall();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [custom, setCustom] = useState('');

  const callSid = active?.callSid || '';
  const { data } = useQuery<ScreenView>({
    queryKey: ['/api/calls/screen', callSid],
    queryFn: async () => (await apiRequest('GET', `/api/calls/${encodeURIComponent(callSid)}/screen`)) as ScreenView,
    enabled: !!callSid,
    refetchInterval: (q: any) => (q?.state?.data?.decision ? false : 1000),
    retry: false,
  });
  const { data: repliesData } = useQuery<{ replies: string[] }>({ queryKey: ['/api/user/quick-replies'], staleTime: 5 * 60 * 1000, enabled: replyOpen });

  // Close shortly after the call leaves screening (decided elsewhere, caller hung up, timed out).
  useEffect(() => {
    if (!active || !data || data.active) return;
    const t = setTimeout(() => screenCallStore.clear(), 2500);
    return () => clearTimeout(t);
  }, [active, data]);

  useEffect(() => { setReplyOpen(false); setCustom(''); }, [callSid]);

  if (!active) return null;

  const decide = async (action: 'answer' | 'voicemail' | 'reply' | 'spam') => {
    if (busy) return;
    setBusy(true);
    try {
      if (action === 'answer') screenCallStore.armAutoAccept(active.number);
      await apiRequest('POST', `/api/calls/${encodeURIComponent(callSid)}/screen/decision`, { action });
      if (action === 'voicemail') toast({ title: 'Sent to voicemail' });
      if (action === 'spam') toast({ title: 'Marked as spam', description: 'The caller was blocked and the call ended.' });
      if (action !== 'answer') screenCallStore.clear();
    } catch (e: any) {
      toast({ title: 'That did not work', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const replyWith = async (text: string) => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await apiRequest('POST', '/api/sms/send', { to: active.number, ...(active.calledNumber ? { from: active.calledNumber } : {}), body });
      setBusy(false);
      await decide('reply');
      toast({ title: 'Message sent', description: body });
    } catch (e: any) {
      setBusy(false);
      toast({ title: 'The text was not sent', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  const state = data?.state || 'screening';
  const label = active.name || formatPhone(active.number) || active.number;

  return (
    <div className="fixed inset-x-0 bottom-0 sm:bottom-6 sm:inset-x-auto sm:right-6 z-[70] p-3 sm:p-0 sm:w-[26rem]" data-testid="screen-call-panel">
      <div className="rounded-2xl bg-white text-slate-900 border border-slate-100 shadow-[0_8px_30px_rgba(15,23,42,0.16)] dark:bg-card dark:text-foreground dark:border-border p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
              <Ear className="h-3 w-3" aria-hidden />Screening
            </span>
            <h2 className="mt-2 text-xl font-semibold truncate">{label}</h2>
            <p className="text-sm text-slate-500 dark:text-muted-foreground" aria-live="polite">{STATUS[state]}</p>
          </div>
          <button type="button" aria-label="Hide" onClick={() => screenCallStore.clear()} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-3 min-h-[3.5rem] rounded-xl bg-slate-50 dark:bg-muted px-3 py-2 text-sm" aria-live="polite" data-testid="screen-call-transcript">
          {data?.transcript
            ? <span className={data.final ? 'text-slate-900 dark:text-foreground' : 'text-slate-600 dark:text-muted-foreground'}>“{data.transcript}”</span>
            : <span className="text-slate-400 italic">Waiting for the caller to speak…</span>}
        </div>

        {data?.active !== false && state !== 'connecting' && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" disabled={busy} onClick={() => decide('answer')} className="h-11 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="screen-answer">
                <Phone className="h-4 w-4" aria-hidden />Answer
              </button>
              <button type="button" disabled={busy} onClick={() => decide('voicemail')} className="h-11 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-muted text-slate-800 dark:text-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="screen-voicemail">
                <Voicemail className="h-4 w-4" aria-hidden />Voicemail
              </button>
              <button type="button" disabled={busy} onClick={() => setReplyOpen((o) => !o)} className="h-11 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-muted text-slate-800 dark:text-foreground font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="screen-reply">
                <MessageSquareText className="h-4 w-4" aria-hidden />Reply with message
              </button>
              <button type="button" disabled={busy} onClick={() => decide('spam')} className="h-11 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="screen-spam">
                <ShieldAlert className="h-4 w-4" aria-hidden />Spam, hang up
              </button>
            </div>
            {replyOpen && (
              <div className="mt-3 space-y-1.5" data-testid="screen-reply-sheet">
                {(repliesData?.replies || []).map((r, i) => (
                  <button key={`${r}-${i}`} type="button" disabled={busy} onClick={() => replyWith(r)} className="w-full text-left rounded-xl bg-slate-50 hover:bg-emerald-50 dark:bg-muted dark:hover:bg-emerald-500/10 px-3 py-2 text-sm disabled:opacity-60">
                    {r}
                  </button>
                ))}
                <form className="flex gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); replyWith(custom); }}>
                  <input value={custom} maxLength={320} onChange={(e) => setCustom(e.target.value)} placeholder="Custom message…" className="flex-1 min-w-0 h-10 rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                  <button type="submit" disabled={busy || !custom.trim()} className="h-10 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60">Send</button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
